import { useCallback, useEffect, useMemo, useState } from "react";
import { CASE_TASK_LABELS, CASE_TASK_TYPES, formatTaskDuration, type CaseTaskType } from "@/lib/caseTasks";
import { useCaseTasks } from "@/lib/CaseTasksContext";
import { useTeam } from "@/lib/useTeam";
import {
  DEFAULT_MANAGER_FILTERS,
  MANAGER_PERIODS,
  MANAGER_STATUSES,
  caseDisplayName,
  completerIds,
  computeKpis,
  filterTasks,
  statsByCenter,
  statsByCommercial,
  type ManagerFilters,
  type ManagerStatusFilter,
  type ManagerTask,
} from "@/lib/managerStats";
import { useManagerTasks } from "./useManagerTasks";
import { ManagerTaskList } from "./ManagerTaskList";
import { CenterStatsTable, CommercialStatsTable } from "./TaskStatsTables";
import { ConfirmDialog, ageTone, personName } from "./managerUi";

const selectClass =
  "h-8 w-48 rounded-md border border-gray-300 bg-white pl-2 pr-7 text-sm text-gray-800 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500";

function Kpi({
  label,
  value,
  hint,
  valueClass = "text-gray-900",
  onClick,
}: {
  label: string;
  value: string;
  hint?: string;
  valueClass?: string;
  onClick?: () => void;
}) {
  const body = (
    <>
      <div className="text-xs font-medium text-gray-500">{label}</div>
      <div className={`mt-1 text-2xl font-semibold tabular-nums ${valueClass}`}>{value}</div>
      <div className="mt-0.5 truncate text-xs text-gray-500">{hint || " "}</div>
    </>
  );
  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      className="rounded-lg border border-gray-200 bg-white px-4 py-3 text-left hover:border-indigo-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
    >
      {body}
    </button>
  ) : (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">{body}</div>
  );
}

export function ManagerTasksTab({ onOpenCase }: { onOpenCase: (caseId: string) => void }) {
  const [filters, setFilters] = useState<ManagerFilters>(DEFAULT_MANAGER_FILTERS);
  const { tasks, loading, error, refresh, patchTask } = useManagerTasks(filters.period, true);
  const { members, profilesById, centers: memberCenters } = useTeam();
  const { cancelTask } = useCaseTasks();
  const [now, setNow] = useState(() => Date.now());
  const [toCancel, setToCancel] = useState<ManagerTask | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  // Les âges des tâches en cours avancent : on rafraîchit l'horloge chaque minute (et à chaque chargement).
  useEffect(() => {
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [tasks]);

  const update = (patch: Partial<ManagerFilters>) => setFilters((prev) => ({ ...prev, ...patch }));
  const nameOf = useCallback((id: string) => personName(profilesById, id), [profilesById]);

  const centerOptions = useMemo(() => {
    const set = new Set<string>(memberCenters);
    for (const task of tasks) {
      if (task.assigned_center) set.add(task.assigned_center);
      if (task.case?.center) set.add(task.case.center);
    }
    return [...set].sort((a, b) => a.localeCompare(b, "fr"));
  }, [memberCenters, tasks]);

  const commercialOptions = useMemo(() => {
    const ids = new Set<string>([...members.map((member) => member.profile_id), ...completerIds(tasks)]);
    return [...ids].map((id) => ({ id, name: nameOf(id) })).sort((a, b) => a.name.localeCompare(b.name, "fr"));
  }, [members, tasks, nameOf]);

  const kpis = useMemo(() => computeKpis(tasks, filters, now), [tasks, filters, now]);
  const centerRows = useMemo(() => statsByCenter(tasks, filters, now, memberCenters), [tasks, filters, now, memberCenters]);
  const commercialRows = useMemo(
    () => statsByCommercial(tasks, filters, now, members, nameOf),
    [tasks, filters, now, members, nameOf],
  );
  const listed = useMemo(() => filterTasks(tasks, filters, now), [tasks, filters, now]);
  const isFiltered =
    filters.center !== null || filters.completedBy !== null || filters.taskType !== null || filters.status !== "all";
  const periodLabel = filters.period === "all" ? "depuis le début" : `sur ${MANAGER_PERIODS.find((p) => p.value === filters.period)?.label}`;

  const confirmCancel = async () => {
    if (!toCancel) return;
    setCancelling(true);
    setCancelError(null);
    try {
      const row = await cancelTask(toCancel.id);
      patchTask(row);
      setToCancel(null);
    } catch (err) {
      setCancelError(err instanceof Error ? err.message : "Annulation impossible. Réessayez.");
      void refresh();
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2" role="group" aria-label="Filtres des tâches">
        <div className="flex items-center rounded-md border border-gray-300 bg-white p-0.5" role="radiogroup" aria-label="Période (date de création)">
          {MANAGER_PERIODS.map((period) => (
            <button
              key={period.value}
              type="button"
              role="radio"
              aria-checked={filters.period === period.value}
              onClick={() => update({ period: period.value })}
              className={`rounded px-2.5 py-1 text-xs font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                filters.period === period.value ? "bg-indigo-600 text-white" : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              {period.label}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-1.5 text-xs text-gray-500">
          Centre
          <select className={selectClass} value={filters.center ?? ""} onChange={(e) => update({ center: e.target.value || null })}>
            <option value="">Tous</option>
            {centerOptions.map((center) => (
              <option key={center} value={center}>
                {center}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1.5 text-xs text-gray-500">
          Cochée par
          <select
            className={selectClass}
            value={filters.completedBy ?? ""}
            onChange={(e) => update({ completedBy: e.target.value || null })}
          >
            <option value="">Tout le monde</option>
            {commercialOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1.5 text-xs text-gray-500">
          Type
          <select
            className={selectClass}
            value={filters.taskType ?? ""}
            onChange={(e) => update({ taskType: (e.target.value || null) as CaseTaskType | null })}
          >
            <option value="">Tous</option>
            {CASE_TASK_TYPES.map((type) => (
              <option key={type} value={type}>
                {CASE_TASK_LABELS[type]}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1.5 text-xs text-gray-500">
          Statut
          <select
            className={selectClass}
            value={filters.status}
            onChange={(e) => update({ status: e.target.value as ManagerStatusFilter })}
          >
            {MANAGER_STATUSES.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
        </label>
        {isFiltered && (
          <button
            type="button"
            onClick={() => setFilters((prev) => ({ ...DEFAULT_MANAGER_FILTERS, period: prev.period }))}
            className="text-xs font-medium text-indigo-600 hover:text-indigo-800 focus:outline-none focus-visible:underline"
          >
            Réinitialiser
          </button>
        )}
        <span className="ml-auto text-xs text-gray-400">Période et liste : date de création · mise à jour en direct</span>
      </div>

      {error && (
        <div role="alert" className="flex items-center justify-between rounded-md border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">
          {error}
          <button type="button" onClick={() => void refresh()} className="font-medium underline">
            Réessayer
          </button>
        </div>
      )}

      {/* Indicateurs */}
      <div className="grid grid-cols-4 gap-3" aria-busy={loading}>
        <Kpi label="En cours" value={loading ? "…" : String(kpis.openCount)} hint="Toutes dates de création" />
        <Kpi label="Terminées" value={loading ? "…" : String(kpis.completedCount)} hint={`Créées ${periodLabel}`} />
        <Kpi
          label="Délai médian de complétion"
          value={loading ? "…" : formatTaskDuration(kpis.medianCompletionMs)}
          hint="De la création à la coche"
        />
        <Kpi
          label="Plus ancienne en cours"
          value={loading ? "…" : formatTaskDuration(kpis.oldestOpenAgeMs)}
          valueClass={ageTone(kpis.oldestOpenAgeMs)}
          hint={
            kpis.oldestOpenTask
              ? `${CASE_TASK_LABELS[kpis.oldestOpenTask.task_type] ?? kpis.oldestOpenTask.task_type} · ${caseDisplayName(kpis.oldestOpenTask.case)}`
              : "Aucune tâche en cours"
          }
          onClick={kpis.oldestOpenTask ? () => onOpenCase(kpis.oldestOpenTask!.case_id) : undefined}
        />
      </div>

      {/* Tableaux */}
      <div className="grid grid-cols-2 items-start gap-4">
        <CenterStatsTable rows={centerRows} loading={loading} onSelectCenter={(center) => update({ center })} />
        <CommercialStatsTable
          rows={commercialRows}
          loading={loading}
          nameOf={nameOf}
          onSelect={(profileId) => update({ completedBy: profileId })}
        />
      </div>

      <ManagerTaskList
        tasks={listed}
        loading={loading}
        now={now}
        profilesById={profilesById}
        filtered={isFiltered || filters.period !== "all"}
        onOpenCase={onOpenCase}
        onCancel={(task) => {
          setCancelError(null);
          setToCancel(task);
        }}
      />

      {toCancel && (
        <ConfirmDialog
          title="Annuler cette tâche ?"
          confirmLabel="Annuler la tâche"
          busy={cancelling}
          onConfirm={() => void confirmCancel()}
          onCancel={() => !cancelling && setToCancel(null)}
        >
          <p>
            « {CASE_TASK_LABELS[toCancel.task_type] ?? toCancel.task_type} » sur{" "}
            <strong className="font-medium text-gray-900">{caseDisplayName(toCancel.case)}</strong> disparaîtra du « À faire »
            de l'équipe. Elle reste visible ici avec le statut « Annulée ».
          </p>
          {cancelError && (
            <p role="alert" className="mt-3 rounded-md bg-red-50 px-3 py-2 text-red-700">
              {cancelError}
            </p>
          )}
        </ConfirmDialog>
      )}
    </div>
  );
}
